/* import __COLOCATED_TEMPLATE__ from './conversation-attribute-updated.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { EntityType } from 'embercom/models/data/entity-types';
import type Session from 'embercom/services/session';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type ConversationAttributeUpdated from 'embercom/objects/inbox/renderable/conversation-attribute-updated';
import LinkWithText from 'embercom/helpers/link-with-text';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import { type WorkflowObject } from 'embercom/objects/inbox/interfaces/workflow';
import type IntlService from 'embercom/services/intl';
import TruncateString from 'embercom/lib/truncate-string';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { DataType } from 'embercom/objects/inbox/conversation-attribute-descriptor';
import moment from 'moment-timezone';
import type Conversation from 'embercom/objects/inbox/conversation';
import type RouterService from '@ember/routing/router-service';
import { post } from 'embercom/lib/ajax';
interface ConversationAttributeUpdatedPart extends RenderablePart {
  renderableData: ConversationAttributeUpdated;
}

interface AnalyticsTracker {
  trackAnalyticsEvent(event: {
    action: string;
    object: string;
    place: string;
    section: string;
  }): void;
}

interface ExplainAttributeResponse {
  explanation: string;
}

interface Args {
  part: ConversationAttributeUpdatedPart;
  conversation: Conversation;
}

interface Signature {
  Args: Args;
}

export default class ConversationAttributeUpdatedComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare intercomEventService: AnalyticsTracker;
  @service declare permissionsService: any;

  MAXIMUM_UNTRUNCATED_LENGTH = 50;
  TRUNCATE_TO = 30;
  ATTRIBUTES_HIDE_OPERATOR: Array<number | string> = ['brand'];

  @tracked explanationText: string | null = null;
  @tracked isLoadingExplanation = false;
  @tracked isExplanationExpanded = false;
  private hasTrackedTooltipHover = false;

  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get translationData() {
    switch (this.renderableData.entityType) {
      case EntityType.Admin:
        if (this.teammate.id === this.session.teammate.id) {
          return this.youTranslationData;
        } else {
          return this.teammateTranslationData;
        }
      case EntityType.Bot:
        if (this.ATTRIBUTES_HIDE_OPERATOR.includes(this.attributeId)) {
          return this.passiveTranslationData;
        } else {
          return this.teammateTranslationData;
        }
      case EntityType.User:
        return this.userTranslationData;
      case EntityType.Workflow:
        return this.workflowTranslationData;
      default: {
        return { key: 'unknown-conversation-attribute-update' };
      }
    }
  }

  get renderableData() {
    return this.args.part.renderableData;
  }

  get isBot() {
    return this.renderableData.entityType === EntityType.Bot;
  }

  get teammate() {
    return this.renderableData.creatingEntity.object as AdminSummary;
  }

  get teammateLink() {
    return this.linkWithTextHelper.compute([
      'apps.app.admins.admin',
      this.teammate.id,
      this.teammate.name,
    ]);
  }

  get value() {
    if (this.validDateTimeValue) {
      // Format datetime in local timezone
      let date = new Date(this.renderableData.value);
      return this.intl.formatDate(date, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    }

    if (this.valueTooLong) {
      return TruncateString(this.renderableData.value, this.TRUNCATE_TO, false);
    } else {
      return this.fullLengthValue;
    }
  }

  get valueWithLineBreaks() {
    return this.fullLengthValue.replaceAll('\n', '<br />');
  }

  get fullLengthValue() {
    return sanitizeHtml(`${this.renderableData.value}`).string;
  }

  get valueTooLong() {
    return this.fullLengthValue.length > this.MAXIMUM_UNTRUNCATED_LENGTH;
  }

  get validDateTimeValue() {
    return (
      this.attributeType === DataType.Datetime &&
      moment.utc(this.renderableData.value, 'YYYY-MM-DD[T]HH:mm:ss[Z]', true).isValid()
    );
  }

  get renderableDataHasValue() {
    return Array.isArray(this.renderableData.value)
      ? this.renderableData.value.length > 0
      : this.renderableData.value != null;
  }

  get youTranslationData() {
    if (this.renderableDataHasValue) {
      return {
        key: 'you-set-value-v2',
        data: {
          attribute: this.attributeName,
          value: this.value,
        },
      };
    } else {
      return {
        key: 'you-unset-value-v2',
        data: {
          attribute: this.attributeName,
        },
      };
    }
  }

  get teammateTranslationData() {
    if (this.renderableDataHasValue) {
      let key;
      if (this.isFinClassificationDataUpdate) {
        key = 'fin-set-ai-category-value-v2';
      } else {
        key = 'entity-set-value-v2';
      }
      return {
        key,
        data: {
          who: this.teammateLink,
          attribute: this.attributeName,
          value: this.value,
        },
      };
    } else {
      return {
        key: 'entity-unset-value-v2',
        data: {
          who: this.teammateLink,
          attribute: this.attributeName,
        },
      };
    }
  }

  get userTranslationData() {
    let user = this.renderableData.entity as UserSummary;

    if (this.renderableDataHasValue) {
      return {
        key: 'entity-set-value-v2',
        data: {
          who: user.displayAs,
          attribute: this.attributeName,
          value: this.value,
        },
      };
    } else {
      return {
        key: 'entity-unset-value-v2',
        data: {
          who: user.displayAs,
          attribute: this.attributeName,
        },
      };
    }
  }

  get passiveTranslationData() {
    if (this.renderableDataHasValue) {
      return {
        key: 'passive-set-value-v2',
        data: {
          attribute: this.attributeName,
          value: this.value,
        },
      };
    } else {
      return {
        key: 'passive-unset-value-v2',
        data: {
          attribute: this.attributeName,
        },
      };
    }
  }

  get workflowTranslationData() {
    let workflow = this.renderableData.entity as WorkflowObject;
    if (this.renderableDataHasValue) {
      return {
        key: 'workflow-set-value-v2',
        data: {
          workflow: workflow.label,
          attribute: this.attributeName,
          value: this.value,
        },
      };
    } else {
      return {
        key: 'workflow-unset-value-v2',
        data: {
          workflow: workflow.label,
          attribute: this.attributeName,
        },
      };
    }
  }

  get attributeName() {
    let attributeSummary = this.renderableData.attributeSummary;
    if (attributeSummary?.isBuiltIn) {
      return this.intl.t(`inbox.ticket-attributes.default-attributes.${attributeSummary.name}`);
    }
    return attributeSummary.name;
  }

  get attributeType() {
    return this.renderableData.attributeSummary.type;
  }

  get attributeId() {
    return this.renderableData.attributeSummary.id;
  }

  get canShowReasoning(): boolean {
    return (
      this.renderableData.isClassificationAttribute &&
      this.renderableData.entityType === EntityType.Bot &&
      (this.session.workspace?.isFeatureEnabled('fin-attributes-show-reasoning-on-demand') ?? false)
    );
  }

  @action
  trackReasoningTooltipHover() {
    if (this.hasTrackedTooltipHover || !this.hasClassificationReason) {
      return;
    }
    this.hasTrackedTooltipHover = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'hovered',
      object: 'classification_reason_tooltip',
      place: 'inbox',
      section: 'conversation_attribute_updated',
    });
  }

  @action
  async fetchExplanation() {
    if (this.explanationText) {
      this.isExplanationExpanded = !this.isExplanationExpanded;
      return;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'show_reasoning_button',
      place: 'inbox',
      section: 'conversation_attribute_updated',
    });

    this.isLoadingExplanation = true;
    this.isExplanationExpanded = true;

    try {
      let response: ExplainAttributeResponse = await post(
        '/ember/fin_attribute_explanations/explain',
        {
          app_id: this.session.workspace.id,
          conversation_id: this.args.conversation.id,
          attribute_identifier: this.renderableData.attributeSummary.id,
          attribute_value: this.renderableData.value,
        },
      );
      this.explanationText =
        response.explanation ||
        this.intl.t('inbox.conversation-attribute-updated.unable-to-generate-explanation');
    } catch (e: unknown) {
      let error = e as {
        jqXHR?: { status?: number; responseJSON?: { required_permission?: string } };
      };
      if (error.jqXHR?.status === 403 && error.jqXHR.responseJSON?.required_permission) {
        this.permissionsService.loadAllAndShowModal(error.jqXHR.responseJSON.required_permission);
        this.isExplanationExpanded = false;
        return;
      }
      this.explanationText = this.intl.t(
        'inbox.conversation-attribute-updated.unable-to-generate-explanation',
      );
    } finally {
      this.isLoadingExplanation = false;
    }
  }

  get isFinClassificationDataUpdate() {
    if (this.canShowReasoning) {
      return (
        this.renderableData.isClassificationAttribute &&
        this.renderableData.entityType === EntityType.Bot
      );
    }
    return (
      this.renderableData.isClassificationAttribute &&
      this.renderableData.entityType === EntityType.Bot &&
      this.hasClassificationReason
    );
  }

  get classificationReason() {
    return this.renderableData.reason;
  }

  get hasClassificationReason() {
    return !!this.classificationReason;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::BotFlowParts::ConversationAttributeUpdated': typeof ConversationAttributeUpdatedComponent;
    'inbox2/conversation-stream/bot-flow-parts/conversation-attribute-updated': typeof ConversationAttributeUpdatedComponent;
  }
}
